// ============================================
// Digital Signs - API Client
// Connects frontend to tRPC backend
// ============================================

const API_BASE = '/api/trpc';

// Helper function for tRPC queries (GET)
async function trpcQuery(procedure) {
  const response = await fetch(`${API_BASE}/${procedure}`, {
    method: 'GET',
    credentials: 'include',
  });
  const data = await response.json();
  if (data.error) {
    throw new Error(data.error.message || 'Request failed');
  }
  return data.result?.data?.json;
}

// Helper function for tRPC mutations (POST)
async function trpcMutate(procedure, input) {
  const response = await fetch(`${API_BASE}/${procedure}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ json: input }),
  });
  const data = await response.json();
  if (data.error) {
    throw new Error(data.error.message || 'Request failed');
  }
  return data.result?.data?.json;
}

// ============================================
// Auth API
// ============================================
export const authApi = {
  login: (email, password) => trpcMutate('auth.login', { email, password }),
  logout: () => trpcMutate('auth.logout', {}),
  me: () => trpcQuery('auth.me'),
  changePassword: (currentPassword, newPassword) => 
    trpcMutate('auth.changePassword', { currentPassword, newPassword }),
};

// ============================================
// Lookups API
// ============================================
export const lookupsApi = {
  getAll: () => trpcQuery('lookups.getAll'),
  getMonitorModels: () => trpcQuery('lookups.getMonitorModels'),
  getMonitorModelsByCategory: () => trpcQuery('lookups.getMonitorModelsByCategory'),
  getBrowserTypes: () => trpcQuery('lookups.getBrowserTypes'),
  getBrowserTypesByCategory: () => trpcQuery('lookups.getBrowserTypesByCategory'),
  getRoles: () => trpcQuery('lookups.getRoles'),
  getDisplayStatuses: () => trpcQuery('lookups.getDisplayStatuses'),
  getContentTypes: () => trpcQuery('lookups.getContentTypes'),
};

// ============================================
// Sub-Accounts API
// ============================================
export const subAccountsApi = {
  list: () => trpcQuery('subAccounts.list'),
  getById: (id) => trpcQuery(`subAccounts.getById?input=${encodeURIComponent(JSON.stringify({ json: { id } }))}`),
  create: (data) => trpcMutate('subAccounts.create', data),
  update: (id, data) => trpcMutate('subAccounts.update', { id, ...data }),
  delete: (id) => trpcMutate('subAccounts.delete', { id }),
};

// ============================================
// Locations API
// ============================================
export const locationsApi = {
  list: () => trpcQuery('locations.list'),
  getById: (id) => trpcQuery(`locations.getById?input=${encodeURIComponent(JSON.stringify({ json: { id } }))}`),
  create: (data) => trpcMutate('locations.create', data),
  update: (id, data) => trpcMutate('locations.update', { id, ...data }),
  delete: (id) => trpcMutate('locations.delete', { id }),
};

// ============================================
// Displays API
// ============================================
export const displaysApi = {
  list: () => trpcQuery('displays.list'),
  getById: (id) => trpcQuery(`displays.getById?input=${encodeURIComponent(JSON.stringify({ json: { id } }))}`),
  create: (data) => trpcMutate('displays.create', data),
  update: (id, data) => trpcMutate('displays.update', { id, ...data }),
  delete: (id) => trpcMutate('displays.delete', { id }),
  regeneratePin: (id) => trpcMutate('displays.regeneratePin', { id }),
  ban: (id, reason) => trpcMutate('displays.ban', { id, reason }),
  unban: (id) => trpcMutate('displays.unban', { id }),
  // Public endpoints for display devices
  verifyPin: (pinCode, fingerprint, publicIp, privateIp, deviceInfo) =>
    trpcMutate('displays.verifyPin', { pinCode, fingerprint, publicIp, privateIp, deviceInfo }),
  getByToken: (displayToken) => 
    trpcQuery(`displays.getByToken?input=${encodeURIComponent(JSON.stringify({ json: { displayToken } }))}`),
  heartbeat: (displayToken) => trpcMutate('displays.heartbeat', { displayToken }),
};

// ============================================
// Content API
// ============================================
export const contentApi = {
  list: (displayId) => trpcQuery(`content.list?input=${encodeURIComponent(JSON.stringify({ json: { displayId } }))}`),
  getForDisplay: (displayId) => 
    trpcQuery(`content.getForDisplay?input=${encodeURIComponent(JSON.stringify({ json: { displayId } }))}`),
  create: (data) => trpcMutate('content.create', data),
  update: (id, data) => trpcMutate('content.update', { id, ...data }),
  delete: (id) => trpcMutate('content.delete', { id }),
  reorder: (items) => trpcMutate('content.reorder', { items }),
};

// ============================================
// Users API
// ============================================
export const usersApi = {
  list: () => trpcQuery('users.list'),
  getById: (id) => trpcQuery(`users.getById?input=${encodeURIComponent(JSON.stringify({ json: { id } }))}`),
  create: (data) => trpcMutate('users.create', data),
  update: (id, data) => trpcMutate('users.update', { id, ...data }),
  delete: (id) => trpcMutate('users.delete', { id }),
  getRoles: () => trpcQuery('users.getRoles'),
};

// ============================================
// Storage API
// ============================================
export const storageApi = {
  listProviders: () => trpcQuery('storage.listProviders'),
  getProvider: (id) => trpcQuery(`storage.getProvider?input=${encodeURIComponent(JSON.stringify({ json: { id } }))}`),
  createProvider: (data) => trpcMutate('storage.createProvider', data),
  updateProvider: (id, data) => trpcMutate('storage.updateProvider', { id, ...data }),
  deleteProvider: (id) => trpcMutate('storage.deleteProvider', { id }),
  testProvider: (id) => trpcMutate('storage.testProvider', { id }),
  getUsage: (subAccountId) => 
    trpcQuery(`storage.getUsage?input=${encodeURIComponent(JSON.stringify({ json: { subAccountId } }))}`),
};

// ============================================
// Export all APIs
// ============================================
export default {
  auth: authApi,
  lookups: lookupsApi,
  subAccounts: subAccountsApi,
  locations: locationsApi,
  displays: displaysApi,
  content: contentApi,
  users: usersApi,
  storage: storageApi,
};
